import { useEffect } from 'react';

/**
 * Mouse drag-to-scroll for a horizontally scrolling row (EN-10545, same ask as
 * the single-view drag: a conventional mouse has no horizontal wheel). Free
 * continuous scrolling — no pagination anchors — so it maps 1:1 onto
 * scrollLeft, and everything that already follows scroll (the header dots, the
 * idle snap) follows a drag for free. Mouse pointers only: touch scrolling is
 * native. Never starts on an interactive element, and after a real drag the
 * trailing click is swallowed so releasing over a card's button can't press it.
 *
 * Extracted from MultiTableGrid (review ask on PR #3721: the 100-line inline
 * effect mixed gesture handling, selection locking and cleanup) so the gesture
 * has its own seam and its own tests.
 *
 * A mouse drag over a page full of TEXT is also a selection gesture and a
 * possible native drag-and-drop, and both fought this one (Jason 8/21): the
 * drag painted blue selection across usernames/balances, and when the press
 * landed on already-selected text the browser started a text-DnD instead —
 * pointer events stop, the drag "doesn't register". Three guards:
 *   • selectstart is cancelled while a potential drag is armed (from
 *     press to release), so no selection ever starts under the gesture;
 *   • dragstart is cancelled on the row outright — nothing on a felt is
 *     ever meant to be picked up and dropped elsewhere;
 *   • crossing the threshold clears any selection a prior click left and
 *     locks body user-select until release (Safari belt-and-braces).
 */
export const DRAG_THRESHOLD_PX = 6;

export const useMouseDragScroll = (
  rowRef: React.RefObject<HTMLElement>,
  enabled: boolean
): void => {
  useEffect(() => {
    const row = rowRef.current;
    if (!row || !enabled) return undefined;
    let startX = 0;
    let startScroll = 0;
    let active = false;
    let dragged = false;
    let suppressClickUntil = 0;
    let prevBodyUserSelect: string | null = null;

    const isInteractive = (t: EventTarget | null): boolean =>
      t instanceof Element &&
      !!t.closest('button, a, input, select, textarea, [role="button"], [role="switch"]');

    const lockSelection = () => {
      window.getSelection?.()?.removeAllRanges();
      prevBodyUserSelect = document.body.style.userSelect;
      document.body.style.userSelect = 'none';
    };
    const unlockSelection = () => {
      if (prevBodyUserSelect === null) return;
      document.body.style.userSelect = prevBodyUserSelect;
      prevBodyUserSelect = null;
    };
    // DIRECT MANIPULATION NEEDS INSTANT WRITES. The row's stylesheet sets
    // scroll-behavior: smooth for the tile-click pagination — but with smooth
    // on, every per-move scrollLeft write below starts an ANIMATION toward
    // that position instead of setting it, sixty retargeted animations a
    // second: the laggy, stop-start "acts like pagination" drag (Jason 8/21).
    // Suspended for exactly the drag's lifetime; clicks keep their glide.
    const lockScrollBehavior = () => {
      row.style.scrollBehavior = 'auto';
    };
    const unlockScrollBehavior = () => {
      row.style.scrollBehavior = '';
    };

    const onDown = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' || e.button !== 0 || isInteractive(e.target)) return;
      active = true;
      dragged = false;
      startX = e.clientX;
      startScroll = row.scrollLeft;
    };
    const onMove = (e: PointerEvent) => {
      if (!active) return;
      // The button is no longer down, so this press is over however it ended.
      // A press that never crossed the drag threshold takes no pointer capture,
      // so releasing OFF the row delivered its pointerup somewhere else and
      // `active` stayed true — after which any button-up move across the row
      // scrolled it (SQE scan, Marlon 8/24). PointerEvent.buttons is the
      // authoritative "is a button still held" bit and needs no extra
      // listeners to unwind.
      if (e.buttons === 0) {
        onUp(e);
        return;
      }
      const dx = e.clientX - startX;
      if (!dragged && Math.abs(dx) < DRAG_THRESHOLD_PX) return;
      if (!dragged) {
        dragged = true;
        row.setPointerCapture?.(e.pointerId);
        row.style.cursor = 'grabbing';
        lockSelection();
        lockScrollBehavior();
      }
      e.preventDefault();
      // Content follows the hand: drag left → reveal what's to the right.
      row.scrollLeft = startScroll - dx;
    };
    const onUp = (e: PointerEvent) => {
      if (!active) return;
      active = false;
      row.style.cursor = '';
      unlockSelection();
      unlockScrollBehavior();
      if (dragged) {
        suppressClickUntil = Date.now() + 150;
        row.releasePointerCapture?.(e.pointerId);
      }
    };
    const swallowClick = (e: MouseEvent) => {
      if (Date.now() < suppressClickUntil) {
        e.stopPropagation();
        e.preventDefault();
      }
    };
    const blockSelectStart = (e: Event) => {
      if (active) e.preventDefault();
    };
    const blockDragStart = (e: Event) => e.preventDefault();

    row.addEventListener('pointerdown', onDown);
    row.addEventListener('pointermove', onMove);
    row.addEventListener('pointerup', onUp);
    row.addEventListener('pointercancel', onUp);
    row.addEventListener('click', swallowClick, true);
    row.addEventListener('selectstart', blockSelectStart);
    row.addEventListener('dragstart', blockDragStart);
    return () => {
      // Exhaustive cleanup, including a teardown that lands MID-drag (review
      // ask on PR #3721): the cursor and the body/scroll locks all reset, so
      // an unmount between press and release can strand nothing.
      row.style.cursor = '';
      unlockSelection();
      unlockScrollBehavior();
      row.removeEventListener('pointerdown', onDown);
      row.removeEventListener('pointermove', onMove);
      row.removeEventListener('pointerup', onUp);
      row.removeEventListener('pointercancel', onUp);
      row.removeEventListener('click', swallowClick, true);
      row.removeEventListener('selectstart', blockSelectStart);
      row.removeEventListener('dragstart', blockDragStart);
    };
  }, [rowRef, enabled]);
};

export default useMouseDragScroll;
